import React, { useEffect, useRef, useState, useCallback } from "react";
import { getReviews, img } from "@/api";
import { Star, ChevronLeft, ChevronRight, ArrowRight, Quote } from "lucide-react";

const GoogleG = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-label="Google" role="img">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5" style={{ color: "#FBBC05" }} aria-label={`${n} star rating`}>
    {[...Array(Math.max(1, Math.min(5, Number(n) || 5)))].map((_, s) => <Star key={s} size={16} fill="currentColor" />)}
  </div>
);

function useCardsPerView() {
  const [n, setN] = useState(1);
  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setN(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return n;
}

export default function ReviewsSlider({ testimonials = [], fallbackPlaceUrl = "" }) {
  const [data, setData] = useState(null);
  const perView = useCardsPerView();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(0);
  const rootRef = useRef(null);
  const [inView, setInView] = useState(false);

  // Only mount/fetch the reviews widget once it scrolls near the viewport, so its
  // async layout changes never contribute to initial-load CLS (it is below the fold).
  useEffect(() => {
    const el = rootRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, { rootMargin: "300px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    let alive = true;
    getReviews().then((d) => { if (alive) setData(d); }).catch(() => setData({ reviews: [] }));
    return () => { alive = false; };
  }, [inView]);

  // Normalise review list: prefer real Google reviews, else CMS testimonials.
  const googleReviews = (data && Array.isArray(data.reviews) ? data.reviews : []).map((r) => ({
    name: r.author_name, photo: r.author_photo, rating: r.rating, text: r.text, time: r.relative_time,
  }));
  const cmsReviews = (testimonials || []).map((t) => ({
    name: t.name, photo: img(t.image), rating: Number(t.rating) || 5, text: t.quote,
    time: [t.role, t.company, t.location].filter(Boolean).join(" · "),
  }));
  const reviews = googleReviews.length > 0 ? googleReviews : cmsReviews;

  const overallRating = (data && data.rating) || 0;
  const totalRatings = (data && data.total_ratings) || 0;
  const placeUrl = (data && data.place_url) || fallbackPlaceUrl;

  const maxIdx = Math.max(0, reviews.length - perView);
  const clamp = useCallback((i) => (i < 0 ? maxIdx : i > maxIdx ? 0 : i), [maxIdx]);
  const next = useCallback(() => setIdx((i) => clamp(i + 1)), [clamp]);
  const prev = useCallback(() => setIdx((i) => clamp(i - 1)), [clamp]);

  useEffect(() => { if (idx > maxIdx) setIdx(0); }, [maxIdx, idx]);

  // Autoplay
  useEffect(() => {
    if (paused || reviews.length <= perView) return;
    const t = setInterval(() => setIdx((i) => clamp(i + 1)), 4500);
    return () => clearInterval(t);
  }, [paused, reviews.length, perView, clamp]);

  if (!inView || !data) {
    return (
      <section ref={rootRef} className="ekta-section" id="reviews" style={{ minHeight: 520 }}>
        <div className="ekta-container">
          <div className="h-[400px] rounded-lg animate-pulse" style={{ background: "var(--bg-2)" }} />
        </div>
      </section>
    );
  }

  if (reviews.length === 0) {
    return (
      <section className="ekta-section" id="reviews" style={{ minHeight: 520 }}>
        <div className="ekta-container text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2.5 mb-3"><GoogleG size={24} /><span className="font-semibold" style={{ color: "var(--ink-2)", fontSize: 15 }}>Google Reviews</span></div>
          <h2 className="ekta-h2">What Our Customers Say</h2>
          <p className="mt-4" style={{ color: "var(--muted-ink)" }}>Verified customer reviews will appear here shortly.</p>
        </div>
      </section>
    );
  }

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx < -40) next(); else if (dx > 40) prev();
  };

  return (
    <section className="ekta-section" id="reviews" data-testid="google-reviews" style={{ minHeight: 520 }}>
      <div className="ekta-container">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
          <div>
            <div className="inline-flex items-center gap-2.5 mb-2"><GoogleG size={24} /><span className="font-semibold" style={{ color: "var(--ink-2)", fontSize: 15 }}>Google Reviews</span></div>
            <h2 className="ekta-h2">What Our Customers Say</h2>
          </div>
          {overallRating > 0 && (
            <div className="flex items-center gap-4 rounded-lg border px-5 py-3" style={{ borderColor: "var(--border-c)", background: "var(--bg-2)" }} data-testid="reviews-rating-badge">
              <div className="text-center">
                <div className="font-bold leading-none" style={{ fontFamily: "var(--font-heading)", fontSize: 32, color: "var(--brand-primary)" }}>{Number(overallRating).toFixed(1)}</div>
                <Stars n={Math.round(overallRating)} />
              </div>
              <div className="text-sm leading-tight" style={{ color: "var(--muted-ink)" }}>
                <div className="font-semibold" style={{ color: "var(--ink)" }}>Excellent</div>
                Based on {totalRatings} Google reviews
              </div>
            </div>
          )}
        </div>

        {/* Slider */}
        <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <div className="overflow-hidden">
            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${idx * (100 / perView)}%)` }}>
              {reviews.map((r, i) => (
                <div key={i} className="shrink-0 px-2.5" style={{ width: `${100 / perView}%` }}>
                  <div className="h-full rounded-lg border bg-white p-6 flex flex-col relative overflow-hidden" style={{ borderColor: "var(--border-c)", boxShadow: "var(--shadow-sm)" }} data-testid="review-card">
                    <Quote size={40} className="absolute -top-1 right-3" style={{ color: "var(--brand-primary)", opacity: 0.06 }} />
                    <div className="flex items-center justify-between mb-3">
                      <Stars n={r.rating} />
                      <GoogleG size={18} />
                    </div>
                    <p className="text-sm flex-1" style={{ color: "var(--ink-2)", lineHeight: 1.7, display: "-webkit-box", WebkitLineClamp: 6, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{r.text}</p>
                    <div className="mt-5 flex items-center gap-3 pt-4 border-t" style={{ borderColor: "var(--border-c)" }}>
                      {r.photo ? <img src={r.photo} alt={r.name} referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full grid place-items-center font-bold text-white shrink-0" style={{ background: "var(--brand-primary)" }}>{(r.name || "?").charAt(0)}</div>}
                      <div className="min-w-0"><div className="font-bold text-sm truncate" style={{ color: "var(--ink)" }}>{r.name}</div>{r.time && <div className="text-xs truncate" style={{ color: "var(--muted-ink)" }}>{r.time}</div>}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Arrows */}
          {reviews.length > perView && (
            <>
              <button onClick={prev} aria-label="Previous reviews" data-testid="reviews-prev" className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 hidden md:flex h-11 w-11 items-center justify-center rounded-full bg-white border shadow-md transition-transform hover:scale-105 active:scale-95" style={{ borderColor: "var(--border-c)", color: "var(--brand-primary)" }}><ChevronLeft size={22} /></button>
              <button onClick={next} aria-label="Next reviews" data-testid="reviews-next" className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 hidden md:flex h-11 w-11 items-center justify-center rounded-full bg-white border shadow-md transition-transform hover:scale-105 active:scale-95" style={{ borderColor: "var(--border-c)", color: "var(--brand-primary)" }}><ChevronRight size={22} /></button>
            </>
          )}
        </div>

        {/* Dots + CTA */}
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          {reviews.length > perView ? (
            <div className="flex gap-2" role="tablist">
              {[...Array(maxIdx + 1)].map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} aria-label={`Go to slide ${i + 1}`} className="h-2 rounded-full transition-all" style={{ width: i === idx ? 26 : 8, background: i === idx ? "var(--brand-primary)" : "var(--border-c)" }} />
              ))}
            </div>
          ) : <span />}
          {placeUrl && (
            <a href={placeUrl} target="_blank" rel="noreferrer" className="ekta-btn ekta-btn-outline" data-testid="reviews-view-all">View All Reviews on Google <ArrowRight size={16} /></a>
          )}
        </div>
      </div>
    </section>
  );
}
